import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, CheckCircle2, ChevronLeft, ChevronRight, Loader2, Plus, RotateCcw, Trash2, ZoomIn, ZoomOut } from 'lucide-react'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Sheet, SheetBody, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useMoney } from '@/components/admin/expenses/moneyUi'
import { getStoredUserId, getStoredUserRole } from '@/lib/staff-roles'
import { toastHelpers } from '@/lib/toast-helpers'
import { formatLocalYMD } from '@/components/admin/expenses/expense-constants'
import { cn } from '@/lib/utils'
import { businessTodayIso, defaultExpiryFor } from '@/lib/expiry'
import { useDayBoundaryHour } from '@/hooks/useDayBoundaryHour'
import { lastPriceHint } from '@/lib/receiptScan/priceChanges'
import {
  DATE_ISSUE_TEXT,
  LINE_PROBLEM_TEXT,
  RECEIPT_STATUS_LABEL,
  emptyLine,
  expensePayload,
  formFromDraft,
  isReading,
  lineProblem,
  lineTotal,
  pendingParts,
  reviewTotals,
  stockPayload,
  type ReviewForm,
  type ReviewLine,
} from '@/lib/receiptScan/receiptScanLogic'
import type { ReceiptScan, ReceiptScanOptions, ReceiptStockPaidBy } from '@/lib/receiptScan/receiptScanTypes'

const EXPENSE_APPROVERS = new Set(['admin', 'manager'])
const STOCK_APPROVERS = new Set(['admin', 'inventory_manager'])
const DISCARDERS = new Set(['admin', 'manager', 'inventory_manager'])

const PAID_BY: { value: ReceiptStockPaidBy; label: string }[] = [
  { value: 'petty_cash', label: 'Petty cash tin' },
  { value: 'company', label: 'Company (vendor bill)' },
  { value: 'buyer', label: 'The buyer, out of pocket' },
]

const NONE = '__none__'

/**
 * The receipt review sheet (F-RPT-N43): the photo beside the draft the AI
 * read. Each line is a Stock line (a stock item, quantity and price — it
 * becomes a stock buy) or an Expense line (a category and amount). The two
 * parts are approved separately, each by the roles that already own that
 * money, so a manager can book the gas bill while the milk waits for the
 * inventory manager.
 */
export function ReceiptReviewSheet({
  scanId,
  onClose,
  onDone,
  queue,
  onNavigate,
}: {
  scanId: string | null
  onClose: () => void
  /** Called after an approval or discard finishes this receipt (batch review moves on). */
  onDone?: (scanId: string) => void
  /** Review mode (F-FE-N192): the receipts being stepped through, in order. */
  queue?: string[]
  onNavigate?: (scanId: string) => void
}) {
  return (
    <Sheet open={!!scanId} onOpenChange={open => !open && onClose()}>
      {/* The mobile admin header (h-14, z-[60]) sits above every sheet, so on a
          phone the sheet starts below it rather than hiding its title and
          queue arrows. Raising the sheet instead would bury its own selects. */}
      <SheetContent size="xl" className="p-0 max-md:top-14 max-md:h-[calc(100dvh-3.5rem)]">
        {scanId && <ReviewBody key={scanId} scanId={scanId} onDone={onDone} queue={queue} onNavigate={onNavigate} />}
      </SheetContent>
    </Sheet>
  )
}

/** Keys that step through the queue, unless the user is typing. */
function isTypingTarget(t: EventTarget | null): boolean {
  const el = t as HTMLElement | null
  if (!el) return false
  return el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName) || el.getAttribute('role') === 'combobox'
}

function ReviewBody({
  scanId,
  onDone,
  queue,
  onNavigate,
}: {
  scanId: string
  onDone?: (scanId: string) => void
  queue?: string[]
  onNavigate?: (scanId: string) => void
}) {
  const pos = queue ? queue.indexOf(scanId) : -1
  const prevId = pos > 0 ? queue![pos - 1] : undefined
  const nextId = pos >= 0 && pos < queue!.length - 1 ? queue![pos + 1] : undefined
  useEffect(() => {
    if (!onNavigate || pos < 0) return
    const onKey = (e: KeyboardEvent) => {
      if (isTypingTarget(e.target) || e.metaKey || e.ctrlKey || e.altKey) return
      if (e.key === 'ArrowRight' && nextId) onNavigate(nextId)
      if (e.key === 'ArrowLeft' && prevId) onNavigate(prevId)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onNavigate, pos, prevId, nextId])
  // A sideways swipe on the photo steps through the queue on a phone.
  const [touchX, setTouchX] = useState<number | null>(null)
  const onTouchEnd = (x: number) => {
    if (touchX === null || !onNavigate) return
    const dx = x - touchX
    setTouchX(null)
    if (dx < -60 && nextId) onNavigate(nextId)
    if (dx > 60 && prevId) onNavigate(prevId)
  }

  const qc = useQueryClient()
  const role = getStoredUserRole()
  const scanQ = useQuery({
    queryKey: ['receiptScan', scanId],
    queryFn: () => apiClient.getReceiptScan(scanId),
    refetchInterval: q => (isReading(q.state.data?.data?.scan.status) ? 2000 : false),
  })
  const optionsQ = useQuery({
    queryKey: ['receiptScanOptions'],
    queryFn: () => apiClient.getReceiptScanOptions(),
    staleTime: 5 * 60_000,
  })
  const imageQ = useQuery({
    queryKey: ['receiptScanImage', scanId],
    queryFn: () => apiClient.getReceiptScanImageUrl(scanId),
    staleTime: Infinity,
    gcTime: 0,
  })
  useEffect(() => {
    const url = imageQ.data
    return () => {
      if (url) URL.revokeObjectURL(url)
    }
  }, [imageQ.data])

  const scan = scanQ.data?.data?.scan
  const duplicates = scanQ.data?.data?.possible_duplicates ?? []
  const options = optionsQ.data?.data
  const [zoom, setZoom] = useState(false)

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['receiptScan', scanId] })
    qc.invalidateQueries({ queryKey: ['receiptScans'] })
    qc.invalidateQueries({ queryKey: ['receiptScanSummary'] })
  }
  const retry = useMutation({
    mutationFn: () => apiClient.retryReceiptScan(scanId),
    onSuccess: refresh,
    onError: e => toastHelpers.apiError('Read again', e),
  })
  const discard = useMutation({
    mutationFn: () => apiClient.discardReceiptScan(scanId),
    onSuccess: () => {
      refresh()
      toastHelpers.success('Receipt discarded')
      onDone?.(scanId)
    },
    onError: e => toastHelpers.apiError('Discard', e),
  })
  const mayDiscard = DISCARDERS.has(role) || scan?.created_by === getStoredUserId()

  return (
    <div className="flex h-full min-h-0 flex-col">
      <SheetHeader className="border-b px-6 py-4">
        <div className="flex items-center gap-3 pr-8">
          <SheetTitle>Review receipt</SheetTitle>
          {pos >= 0 && queue && queue.length > 1 && (
            <div className="ml-auto flex items-center gap-1 text-sm text-muted-foreground">
              <Button type="button" variant="ghost" size="icon" className="h-8 w-8" disabled={!prevId}
                onClick={() => prevId && onNavigate?.(prevId)} aria-label="Previous receipt">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="tabular-nums">
                {pos + 1} of {queue.length}
              </span>
              <Button type="button" variant="ghost" size="icon" className="h-8 w-8" disabled={!nextId}
                onClick={() => nextId && onNavigate?.(nextId)} aria-label="Next receipt">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
        {scan && (
          <p className="text-sm text-muted-foreground">
            {RECEIPT_STATUS_LABEL[scan.status]}
            {scan.created_by_name ? ` · scanned by ${scan.created_by_name}` : ''}
            {scan.source === 'whatsapp' ? ' · sent on WhatsApp' : ''}
          </p>
        )}
      </SheetHeader>
      <div className="grid min-h-0 flex-1 grid-cols-1 overflow-y-auto lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:grid-rows-[minmax(0,1fr)] lg:overflow-hidden">
        {/* The photo. Tap to zoom; it scrolls when zoomed. */}
        <div
          className="relative max-h-[45vh] min-h-[240px] overflow-auto border-b bg-muted/40 lg:max-h-none lg:border-b-0 lg:border-r"
          onTouchStart={e => !zoom && setTouchX(e.touches[0].clientX)}
          onTouchEnd={e => !zoom && onTouchEnd(e.changedTouches[0].clientX)}
        >
          {imageQ.data ? (
            <button type="button" className="block w-full" onClick={() => setZoom(z => !z)} aria-label={zoom ? 'Zoom out' : 'Zoom in'}>
              <img
                src={imageQ.data}
                alt="Receipt"
                className={cn('mx-auto', zoom ? 'max-w-none w-[200%] cursor-zoom-out' : 'max-h-[70vh] w-auto max-w-full object-contain p-4 cursor-zoom-in')}
              />
            </button>
          ) : (
            <div className="flex h-full items-center justify-center p-8 text-sm text-muted-foreground">
              {imageQ.isError ? "The photo couldn't be loaded." : <Loader2 className="h-5 w-5 animate-spin" />}
            </div>
          )}
          {imageQ.data && (
            <span className="pointer-events-none absolute right-3 top-3 rounded-md bg-background/90 p-1.5 text-muted-foreground shadow-sm">
              {zoom ? <ZoomOut className="h-4 w-4" /> : <ZoomIn className="h-4 w-4" />}
            </span>
          )}
        </div>

        <div className="flex flex-col lg:min-h-0">
          {!scan || !options ? (
            <div className="flex flex-1 items-center justify-center p-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : isReading(scan.status) ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <p className="font-medium">Reading the receipt…</p>
              <p className="max-w-sm text-sm text-muted-foreground">
                This usually takes a few seconds. You can close this — it will be waiting in Receipts.
              </p>
            </div>
          ) : scan.status === 'failed' ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
              <AlertTriangle className="h-6 w-6 text-amber-600" />
              <p className="max-w-sm text-sm">{scan.error || "We couldn't read this photo."}</p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => retry.mutate()} disabled={retry.isPending}>
                  <RotateCcw className="mr-1 h-4 w-4" /> Read again
                </Button>
                {mayDiscard && (
                  <Button variant="ghost" onClick={() => discard.mutate()} disabled={discard.isPending}>
                    Discard
                  </Button>
                )}
              </div>
            </div>
          ) : scan.status === 'approved' || scan.status === 'discarded' ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
              <CheckCircle2 className="h-6 w-6 text-muted-foreground" />
              <p className="font-medium">{RECEIPT_STATUS_LABEL[scan.status]}</p>
              {scan.expense_ids.length > 0 && (
                <p className="text-sm text-muted-foreground">
                  {scan.expense_ids.length} expense {scan.expense_ids.length === 1 ? 'entry' : 'entries'} recorded
                </p>
              )}
              {scan.purchase_order_id && <p className="text-sm text-muted-foreground">Stock buy recorded in Purchasing</p>}
            </div>
          ) : (
            <ReviewForm
              scan={scan}
              options={options}
              duplicates={duplicates}
              role={role}
              onApproved={finished => {
                refresh()
                qc.invalidateQueries({ queryKey: ['expenses'] })
                qc.invalidateQueries({ queryKey: ['expenseSummary'] })
                qc.invalidateQueries({ queryKey: ['pnlReport'] })
                if (finished) onDone?.(scanId)
              }}
              onDiscard={mayDiscard ? () => discard.mutate() : undefined}
              discarding={discard.isPending}
            />
          )}
        </div>
      </div>
    </div>
  )
}

function ReviewForm({
  scan,
  options,
  duplicates,
  role,
  onApproved,
  onDiscard,
  discarding,
}: {
  scan: ReceiptScan
  options: ReceiptScanOptions
  duplicates: { kind: string; id: string; date: string; amount?: number; category?: string }[]
  role: string
  onApproved: (finished: boolean) => void
  onDiscard?: () => void
  discarding: boolean
}) {
  const money = useMoney()
  const [form, setForm] = useState<ReviewForm>(() => formFromDraft(scan.draft, options, formatLocalYMD(new Date())))
  const set = (patch: Partial<ReviewForm>) => setForm(f => ({ ...f, ...patch }))
  // Changing what a line is matched to means it is no longer the learned match.
  const setLine = (key: string, patch: Partial<ReviewLine>) =>
    setForm(f => ({
      ...f,
      lines: f.lines.map(l =>
        l.key === key ? { ...l, ...patch, ...('kind' in patch || 'category' in patch || 'stockItemId' in patch ? { learned: false } : {}) } : l,
      ),
    }))
  const totals = reviewTotals(form)
  const pending = pendingParts(scan, form)
  const expenseLocked = !!scan.expense_approved_at
  const stockLocked = !!scan.stock_approved_at
  const checks = scan.draft?.checks
  const items = useMemo(() => new Map(options.stock_items.map(i => [i.id, i])), [options.stock_items])

  const expenseProblem = form.lines.filter(l => l.kind === 'expense').map(l => lineProblem(l)).find(Boolean)
  // Pilot Batch 5: an expiry-tracked item needs its use-by date to be approved.
  const stockProblem = form.lines.filter(l => l.kind === 'stock').map(l => lineProblem(l, items.get(l.stockItemId))).find(Boolean)
  const today = businessTodayIso(useDayBoundaryHour())
  const canApproveExpenses = EXPENSE_APPROVERS.has(role) && pending.expense
  const canApproveStock = STOCK_APPROVERS.has(role) && pending.stock

  // F-RPT-N47: a vendor not on the list can be added here, so the next
  // receipt from them is recognised. Purchasing's own rule: admin or
  // inventory manager.
  const qc = useQueryClient()
  const printedVendor = scan.draft?.extraction.vendor_name?.trim() ?? ''
  const canAddVendor = STOCK_APPROVERS.has(role) && !form.supplierId && (form.payee.trim() || printedVendor) !== ''
  const addVendor = useMutation({
    mutationFn: () => apiClient.createSupplier({ name: (form.payee.trim() || printedVendor).slice(0, 150) }),
    onSuccess: async res => {
      await qc.invalidateQueries({ queryKey: ['receiptScanOptions'] })
      if (res.data?.id) set({ supplierId: res.data.id })
      toastHelpers.success('Vendor added', 'Receipts from them will be recognised next time.')
    },
    onError: e => toastHelpers.apiError('Add vendor', e),
  })

  const approveExpenses = useMutation({
    mutationFn: () => apiClient.approveReceiptExpenses(scan.id, expensePayload(form, pending.stock)),
    onSuccess: res => {
      toastHelpers.success('Expenses recorded', `${money.full(totals.expense)} on ${form.date}`)
      // Done for this person once nothing is left that they can approve.
      onApproved(res.data?.status === 'approved' || !STOCK_APPROVERS.has(role))
    },
    onError: e => toastHelpers.apiError('Approve expenses', e),
  })
  const approveStock = useMutation({
    mutationFn: () => apiClient.approveReceiptStock(scan.id, stockPayload(form, options, pending.expense)),
    onSuccess: res => {
      toastHelpers.success('Stock recorded', `${money.full(totals.stock)} — it's in Purchasing`)
      onApproved(res.data?.status === 'approved' || !EXPENSE_APPROVERS.has(role))
    },
    onError: e => toastHelpers.apiError('Approve stock', e),
  })

  const nextKey = () => `n${Date.now()}`

  return (
    <>
      <SheetBody className="flex-1 space-y-5 px-6 py-5 lg:min-h-0 lg:overflow-y-auto">
        {/* What needs a second look. */}
        <div className="space-y-2">
          {duplicates.length > 0 && (
            <Notice>
              {duplicates.some(d => d.kind === 'scan') ? 'This photo was scanned before. ' : ''}
              {duplicates.some(d => d.kind === 'expense')
                ? `An expense of the same amount is already recorded on ${duplicates.find(d => d.kind === 'expense')?.date}. `
                : ''}
              Check it isn't a duplicate.
            </Notice>
          )}
          {checks?.date_issue ? <Notice>{DATE_ISSUE_TEXT[checks.date_issue]}</Notice> : null}
          {Math.abs(totals.difference) >= 1 && totals.receiptTotal > 0 && (
            <Notice>
              The lines add up to {money.full(totals.lines)}, the receipt total is {money.full(totals.receiptTotal)}. A
              difference of {money.full(totals.difference)} — tax, a discount, or a line that was missed?
            </Notice>
          )}
          {scan.draft?.extraction.notes ? <p className="text-sm text-muted-foreground">Note from the reader: {scan.draft.extraction.notes}</p> : null}
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="grid gap-2">
            <Label>Vendor</Label>
            <Select value={form.supplierId || NONE} onValueChange={v => set({ supplierId: v === NONE ? '' : v })}>
              <SelectTrigger className={cn(!form.supplierId && (scan.draft?.extraction.vendor_confidence ?? 1) < 0.75 && 'border-amber-500')}>
                <SelectValue placeholder="Not on the list" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>Not on the list</SelectItem>
                {options.suppliers.map(s => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {scan.draft?.supplier_from_history && form.supplierId === scan.draft.supplier_id && <LearnedNote />}
          </div>
          {!form.supplierId && (
            <div className="grid gap-2">
              <Label>Paid to</Label>
              <Input value={form.payee} onChange={e => set({ payee: e.target.value })} placeholder="Shop or person" maxLength={150} />
              {canAddVendor && (
                <Button type="button" variant="link" size="sm" className="h-auto justify-start p-0" disabled={addVendor.isPending}
                  onClick={() => addVendor.mutate()}>
                  <Plus className="mr-1 h-3.5 w-3.5" /> Add “{(form.payee.trim() || printedVendor).slice(0, 40)}” as a vendor
                </Button>
              )}
            </div>
          )}
          <div className="grid gap-2">
            <Label>Receipt date</Label>
            <Input
              type="date"
              value={form.date}
              max={formatLocalYMD(new Date())}
              onChange={e => set({ date: e.target.value })}
              className={cn(checks?.date_issue && 'border-amber-500')}
            />
          </div>
          <div className="grid gap-2">
            <Label>Receipt total</Label>
            <Input inputMode="decimal" value={form.total} onChange={e => set({ total: e.target.value })} />
          </div>
          <div className="grid gap-2">
            <Label>Payment method</Label>
            <Select value={form.paymentMethod || NONE} onValueChange={v => set({ paymentMethod: v === NONE ? '' : (v as ReviewForm['paymentMethod']) })}>
              <SelectTrigger>
                <SelectValue placeholder="Not recorded" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>Not recorded</SelectItem>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="bank">Bank transfer</SelectItem>
                <SelectItem value="card">Card</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label>Receipt no.</Label>
            <Input value={form.referenceNo} onChange={e => set({ referenceNo: e.target.value })} maxLength={120} />
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-baseline justify-between">
            <h3 className="text-sm font-semibold">Lines</h3>
            <span className="text-xs text-muted-foreground">Stock lines become a stock buy; expense lines go to the ledger.</span>
          </div>
          {form.lines.length === 0 && <p className="text-sm text-muted-foreground">No lines were read. Add them below.</p>}
          {form.lines.map(l => {
            const locked = l.kind === 'expense' ? expenseLocked : stockLocked
            const item = items.get(l.stockItemId)
            const problem = lineProblem(l, l.kind === 'stock' ? item : undefined)
            return (
              <div key={l.key} className={cn('space-y-2 rounded-lg border p-3', l.unsure && !locked && 'border-amber-500/70', locked && 'opacity-60')}>
                {l.learned && !locked && <LearnedNote />}
                <div className="flex flex-wrap items-center gap-2">
                  <Input
                    value={l.description}
                    onChange={e => setLine(l.key, { description: e.target.value })}
                    placeholder="What it was"
                    className="h-9 min-w-[160px] flex-1"
                    disabled={locked}
                  />
                  <div className="flex rounded-lg border p-0.5 text-sm">
                    {(['stock', 'expense'] as const).map(k => (
                      <button
                        key={k}
                        type="button"
                        disabled={locked || (k === 'expense' ? expenseLocked : stockLocked)}
                        onClick={() => setLine(l.key, { kind: k })}
                        className={cn('rounded-md px-2.5 py-1 capitalize', l.kind === k ? 'bg-primary text-primary-foreground' : 'text-muted-foreground')}
                      >
                        {k}
                      </button>
                    ))}
                  </div>
                  <span className="w-28 text-right text-sm font-medium tabular-nums">{money.full(lineTotal(l))}</span>
                  {!locked && (
                    <Button type="button" variant="ghost" size="icon" className="h-8 w-8" aria-label="Remove line"
                      onClick={() => setForm(f => ({ ...f, lines: f.lines.filter(x => x.key !== l.key) }))}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                {l.kind === 'expense' ? (
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-[minmax(0,1fr)_140px]">
                    <Select value={l.category || NONE} onValueChange={v => setLine(l.key, { category: v === NONE ? '' : v })} disabled={locked}>
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NONE}>Pick a category</SelectItem>
                        {options.categories.map(c => (
                          <SelectItem key={c.slug} value={c.slug}>
                            {c.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input inputMode="decimal" className="h-9" placeholder="Amount" value={l.amount} disabled={locked}
                      onChange={e => setLine(l.key, { amount: e.target.value })} />
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-[minmax(0,1fr)_90px_110px_120px]">
                    <div className="col-span-2 sm:col-span-1">
                      <Select value={l.stockItemId || NONE} disabled={locked}
                        onValueChange={v => {
                          const picked = v === NONE ? undefined : items.get(v)
                          setLine(l.key, { stockItemId: v === NONE ? '' : v, unitMode: 'base', expiryDate: picked ? defaultExpiryFor(picked, today) : '' })
                        }}>
                        <SelectTrigger className="h-9">
                          <SelectValue placeholder="Stock item" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={NONE}>Pick the stock item</SelectItem>
                          {options.stock_items.map(i => (
                            <SelectItem key={i.id} value={i.id}>
                              {i.name} ({i.unit})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Input inputMode="decimal" className="h-9" placeholder="Qty" value={l.qty} disabled={locked}
                      onChange={e => setLine(l.key, { qty: e.target.value })} />
                    {item?.purchase_unit_label && item.purchase_unit_factor ? (
                      <Select value={l.unitMode} disabled={locked} onValueChange={v => setLine(l.key, { unitMode: v as 'base' | 'purchase' })}>
                        <SelectTrigger className="h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="base">{item.unit}</SelectItem>
                          <SelectItem value="purchase">
                            {item.purchase_unit_label} ({item.purchase_unit_factor} {item.unit})
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className="flex h-9 items-center px-2 text-sm text-muted-foreground">{item?.unit || l.rawUnit || 'unit'}</span>
                    )}
                    <Input inputMode="decimal" className="h-9" placeholder="Price each" value={l.unitPrice} disabled={locked}
                      onChange={e => setLine(l.key, { unitPrice: e.target.value })} />
                    {/* Kitchen flow pilot (Batch 5): the lot's use-by date and
                        number — required for an item tracked for expiry. */}
                    <label className="col-span-2 flex flex-wrap items-center gap-2 text-sm sm:col-span-4">
                      <span className={cn('text-muted-foreground', item?.tracks_expiry && !l.expiryDate && 'font-medium text-danger-fg')}>
                        {item?.tracks_expiry ? 'Use by*' : 'Use by'}
                      </span>
                      <Input type="date" className="h-9 w-44" aria-label="Use by" aria-required={Boolean(item?.tracks_expiry)} value={l.expiryDate ?? ''} disabled={locked}
                        onChange={e => setLine(l.key, { expiryDate: e.target.value })} />
                      <Input className="h-9 w-32" placeholder="Lot no." aria-label="Lot no." value={l.lotNo ?? ''} disabled={locked} maxLength={60}
                        onChange={e => setLine(l.key, { lotNo: e.target.value })} />
                    </label>
                  </div>
                )}
                {problem && !locked && <p className="text-xs text-amber-700 dark:text-amber-400">{LINE_PROBLEM_TEXT[problem]}</p>}
                {l.kind === 'stock' && item?.last_unit_cost ? <LastPrice line={l} item={item} /> : null}
              </div>
            )
          })}
          <div className="flex gap-2">
            {!stockLocked && (
              <Button type="button" variant="outline" size="sm" onClick={() => set({ lines: [...form.lines, emptyLine(nextKey(), 'stock')] })}>
                <Plus className="mr-1 h-4 w-4" /> Stock line
              </Button>
            )}
            {!expenseLocked && (
              <Button type="button" variant="outline" size="sm" onClick={() => set({ lines: [...form.lines, emptyLine(nextKey(), 'expense')] })}>
                <Plus className="mr-1 h-4 w-4" /> Expense line
              </Button>
            )}
          </div>
        </div>

        {pending.stock && (
          <div className="space-y-2 rounded-lg border bg-muted/30 p-3 text-sm">
            {options.kitchen_flow ? (
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <div className="grid gap-1.5">
                  <Label>Stock was paid from</Label>
                  <Select value={form.paidBy} onValueChange={v => set({ paidBy: v as ReceiptStockPaidBy })}>
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAID_BY.map(p => (
                        <SelectItem key={p.value} value={p.value}>
                          {p.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {form.paidBy === 'buyer' && (
                  <div className="grid gap-1.5">
                    <Label>Who paid</Label>
                    <Input className="h-9" value={form.buyerName} onChange={e => set({ buyerName: e.target.value })} maxLength={100} />
                  </div>
                )}
              </div>
            ) : (
              <p className="text-muted-foreground">The stock is recorded as a vendor bill to settle from Vendors, like a quick purchase.</p>
            )}
            <p className="text-muted-foreground">Stock is added to today's count; the receipt number stays on the purchase.</p>
            {!STOCK_APPROVERS.has(role) && <p className="text-muted-foreground">Stock lines are approved by an admin or the inventory manager.</p>}
          </div>
        )}
        {pending.expense && !EXPENSE_APPROVERS.has(role) && (
          <p className="text-sm text-muted-foreground">Expense lines are approved by an admin or a manager.</p>
        )}
      </SheetBody>

      <SheetFooter className="sticky bottom-0 flex-wrap items-center gap-2 border-t bg-background px-6 py-4 sm:justify-between">
        <div className="text-sm text-muted-foreground">
          Expenses <span className="font-medium text-foreground tabular-nums">{money.full(totals.expense)}</span>
          {' · '}Stock <span className="font-medium text-foreground tabular-nums">{money.full(totals.stock)}</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {onDiscard && (
            <Button variant="ghost" onClick={onDiscard} disabled={discarding}>
              Discard
            </Button>
          )}
          {canApproveExpenses && (
            <Button
              variant={canApproveStock ? 'outline' : 'default'}
              disabled={!!expenseProblem || !form.date || approveExpenses.isPending}
              title={expenseProblem ? LINE_PROBLEM_TEXT[expenseProblem] : undefined}
              onClick={() => approveExpenses.mutate()}
            >
              {approveExpenses.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
              Approve expenses
            </Button>
          )}
          {canApproveStock && (
            <Button
              disabled={!!stockProblem || approveStock.isPending}
              title={stockProblem ? LINE_PROBLEM_TEXT[stockProblem] : undefined}
              onClick={() => approveStock.mutate()}
            >
              {approveStock.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
              Approve stock
            </Button>
          )}
        </div>
      </SheetFooter>
    </>
  )
}

/** "Last bought at …" under a stock line (F-RPT-N46), flagged when this
 *  receipt's price is 10% or more away from it. Compared per stock unit, so a
 *  line entered in bags is converted first. */
function LastPrice({
  line,
  item,
}: {
  line: ReviewLine
  item: ReceiptScanOptions['stock_items'][number]
}) {
  const money = useMoney()
  const factor = line.unitMode === 'purchase' && item.purchase_unit_factor ? item.purchase_unit_factor : 1
  const perUnit = (Number(line.unitPrice) || 0) / factor
  const hint = lastPriceHint(perUnit, { cost: item.last_unit_cost, on: item.last_bought_on })
  if (!hint) return null
  const on = item.last_bought_on ? new Date(`${item.last_bought_on}T12:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : ''
  return (
    <p className={cn('text-xs', hint.jump ? 'text-amber-700 dark:text-amber-400' : 'text-muted-foreground')}>
      Last bought at {money.full(item.last_unit_cost ?? 0)} per {item.unit}
      {on ? ` on ${on}` : ''}
      {hint.jump ? ` — this receipt is ${Math.abs(hint.pct)}% ${hint.jump === 'up' ? 'higher' : 'lower'}` : ''}
    </p>
  )
}

/** Green mark on a field filled from an earlier approved receipt (F-RPT-N47). */
function LearnedNote() {
  return (
    <p className="flex items-center gap-1 text-xs text-success-fg">
      <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
      Matched from past receipts
    </p>
  )
}

function Notice({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex gap-2 rounded-lg border border-amber-500/50 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <span>{children}</span>
    </div>
  )
}
